<template>
  <div class="filter-search-product-grid product-grid">
    <template v-if="items.length === 0">
      <slot name="noResults">
        <div class="product-grid__no-results">
          <p
            class="text-body-s-regular"
            v-text="$string('collection.no_results')"
          />
        </div>
      </slot>
    </template>

    <div
      v-if="products.length"
      class="product-grid__grid grid"
      :class="{ 'two-column': mobileColumnCount === 2 }"
    >
      <template
        v-for="(card, index) of (activeFilters.length ? products : allCards)"
        :key="`product-${card.handle}-${index}`"
      >
        <content-card
          v-if="card.type === 'content_card'"
          class="product-grid__item--content col"
          :content="card"
          :mobile-column-count="mobileColumnCount"
          :small-card="mobileColumnCount === 2"
          :style="getContentCardStyles(card, index)"
        />
        <div
          v-else
          class="product-grid__item col"
        >
          <product-card
            :ref="setItemRefs"
            :product="card"
            :lazyload="index > 4"
            :loaded-animation="index > 4"
            :small-card="mobileColumnCount === 2"
            :image-options="{
              sizes: `(min-width: ${breakpoints.l}) 22vw, 50vw`
            }"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Filter Search Product Grid (filter-search-product-grid)
 * -----------------------------------------------------------------------------
 * Component to display products in a grid and handling load more/load previous
 * pagination.
 *
 * @param {Array} products - Array of formatted product card data.
 *
 */

import { mapState } from 'vuex'

import ProductCard from '~global/product-card/product-card'
import ContentCard from '~async/filter-search/content-card/content-card'

import breakpoints from '~/config/breakpoints'

export default {
  name: 'FilterSearchProductGrid',

  components: { ProductCard, ContentCard },

  props: {
    activeFilters: {
      type: Array,
      default: () => [],
    },
    mobileColumnCount: {
      type: Number,
      default: 1,
    },
    products: {
      type: Array,
      default: () => [],
    },
    collectionData: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
        totalProducts: 0,
      }),
    },
    contentCards: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      itemRefs: [],
      currentPage: 1,
      staticFilters: [],
      pageType: window.cnvs.page.type,
      pagination: this.collectionData.pageInfo,
      totalProducts: this.collectionData.totalProducts,
      perPage: this.$variable('settings.pagination.products'),
      breakpoints,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute array of all items to display in the grid.
     * - Injects in grid content in with the products array.
     * @returns {Array} combined array of products and in grid content.
     */
    items() {
      const items = [...this.products]

      return items
    },

    /**
     * Compute product and content cards array.
     * @returns {[]}
     */
    allCards() {
      const cards = this.products.slice()

      const position = this.isMobile ? 'mobile_position' : 'position'

      const contentCards = this.contentCards.slice()
      contentCards.sort((a, b) => {
        return a[position] - b[position]
      })

      contentCards.forEach((card) => {
        const cardIndex = card[position] - 1
        if (card.page !== this.pagination.currentPage || cardIndex < 0) {
          return
        }
        cards.splice(cardIndex, 0, {
          ...card,
        })
      })

      return cards
    },
  },

  mounted() {
    this.getCurrentPage()
    this.redirectPage()
  },

  beforeUpdate() {

    /**
     * Clear out the item refs array when the component updates.
     * - Avoids duplicate refs in the array.
     */
    this.itemRefs = []
  },

  methods: {

    /**
     * Get current page
     */
    getCurrentPage() {
      const urlParams = new URLSearchParams(window.location.search)
      const page = urlParams.get('page')

      if (page) {
        this.currentPage = Number(page)
      }
    },

    /**
     * Redirect page to first
     */
    redirectPage() {
      if (this.currentPage > 1 && !this.products.length) {
        const params = new URLSearchParams(window.location.search)
        params.set('page', '1')
        window.location.href = `${window.location.pathname}?${params.toString()}`
      }
    },

    /**
     * Set product card item refs.
     * @param {HTMLElement} element - Product card in v-for.
     */
    setItemRefs(element) {
      if (!element?.$el) {
        return
      }

      this.itemRefs.push(element.$el)
    },

    /**
     * Dynamically determine if aspect ratio should be used for in grid banners.
     * If there is an adjacent product card, remove the ratio so that the card
     * heights are the same.
     * Only applies to single column cards on mobile.
     * @param card - The content card
     * @param index - The card's index in array
     * @returns {{aspectRatio: string}|{}}
     */
    getContentCardStyles(card, index) {
      const { mobile_col } = card

      // eslint-disable-next-line camelcase
      if (card.type !== 'content_card' || !this.isMobile || Number(mobile_col) !== 1) {
        return {}
      }

      const previousCard = this.allCards[index - 1] || {}
      const nextCard = this.allCards[index + 1] || {}
      const isEven = (index + 1) % 2 === 0

      const hasAdjacentProduct = (isEven && previousCard.type !== 'content_card') ||
        (!isEven && nextCard.type !== 'content_card')

      if (hasAdjacentProduct) {
        return {
          aspectRatio: 'unset',
        }
      }

      return {}
    },
  },
}
</script>

<style lang="scss">
@import './filter-search-product-grid';
</style>
